import { useCallback, useRef } from 'react';

import type { UUID } from '@aline/common';
import { useApolloClient } from '@apollo/client/react';
import { Apollo } from 'src/gql/generated';
import { agentThreadTypename } from 'src/helpers';
import { v4 as uuid4 } from 'uuid';

/** Starts Aline AI from an activity composer with its prompt and the skills it pins. */
export type OnSubmitAiFn = (
  prompt: string,
  skills: Apollo.AgentSkillReferenceInput[]
) => Promise<boolean>;

interface UseStartAiThreadArgs {
  taskId?: UUID;
  workflowId?: UUID;
}

/**
 * Builds the `onSubmitAI` handler for an activity composer: receives the markdown prompt and the
 * skills it pins, and starts an agent thread tied to the task/workflow. The activity subscription
 * adds the new thread row. Resolves true on success so the composer clears.
 */
export function useStartAiThreadFromActivity({
  taskId,
  workflowId,
}: UseStartAiThreadArgs): OnSubmitAiFn {
  const apolloClient = useApolloClient();
  const submittingRef = useRef(false);
  const [startActivityAgentThread] = Apollo.useStartActivityAgentThreadMutation();

  return useCallback(
    async (prompt: string, skills: Apollo.AgentSkillReferenceInput[]): Promise<boolean> => {
      const trimmedPrompt = prompt.trim();
      if (!trimmedPrompt) return false;
      /**
       * Guard against a double submit (rapid Enter / double-click) starting two agent runs while
       * the first mutation is still in flight.
       */
      if (submittingRef.current) return false;
      submittingRef.current = true;

      const assistantMessageId = uuid4();
      const threadId = uuid4();
      const userMessageId = uuid4();

      try {
        const { data, error } = await startActivityAgentThread({
          variables: {
            input: {
              agentSkills: skills,
              assistantMessageId,
              prompt: trimmedPrompt,
              taskId,
              threadId,
              userMessageId,
              workflowId,
            },
          },
        });
        if (error || !data) return false;

        /**
         * Seed the thread into the cache the same way useSendUserMessage does for a new chat: the
         * drawer's ChatPanel reads GetAgentThread (cache-and-network), so without this the panel is
         * blank until the network resolves. The global useStreamingAgentEvents subscription then
         * patches the assistant message (by id) as it streams. The server's GetAgentThread resolver
         * returns these same rows on reload, and the additive messages merge keeps them in sync.
         * The user text uses the server-resolved prompt (which includes the auto-attached document
         * mentions) so the seeded bubble matches the persisted row and doesn't rewrite on refetch.
         */
        const createdAt = new Date().toISOString();
        apolloClient.cache.writeQuery<
          Apollo.GetAgentThreadQuery,
          Apollo.GetAgentThreadQueryVariables
        >({
          data: {
            __typename: 'Query',
            agentThread: {
              __typename: agentThreadTypename,
              artifacts: [],
              context: [],
              id: threadId,
              messages: [
                {
                  __typename: 'AgentMessage' as const,
                  createdAt,
                  data: {},
                  feedback: null,
                  id: userMessageId,
                  planState: null,
                  role: Apollo.AgentMessageRole.User,
                  status: Apollo.AgentMessageStatus.Complete,
                  text: data.startActivityAgentThread.prompt,
                },
                {
                  __typename: 'AgentMessage' as const,
                  createdAt,
                  data: { suggestionStates: {} },
                  feedback: null,
                  id: assistantMessageId,
                  planState: null,
                  role: Apollo.AgentMessageRole.Assistant,
                  status: Apollo.AgentMessageStatus.Streaming,
                  text: '',
                },
              ],
              title: 'Chat',
            },
          },
          query: Apollo.GetAgentThreadDocument,
          variables: { id: threadId },
        });

        return true;
      } catch {
        /**
         * A rejected mutate (network error) would otherwise propagate through the composer's Enter
         * handler unobserved. The global Apollo errorLink surfaces the failure; resolving false
         * keeps the draft in the composer for a retry.
         */
        return false;
      } finally {
        submittingRef.current = false;
      }
    },
    [apolloClient, startActivityAgentThread, taskId, workflowId]
  );
}
